/**
 * Yahoo stamps the in-progress bar at "now" (or HH:59:59) instead of the
 * candle open. That stamp moves on every refetch, so the same candle is stored
 * again under a new timestamp. Snap it back onto the open the rest of the
 * series uses, and fold it into that candle.
 */

function toMs(bar) {
  if (Number.isFinite(Number(bar?.Epoch)) && Number(bar.Epoch) > 0) return Number(bar.Epoch);
  const parsed = Date.parse(bar?.TimeStamp);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function offsetOf(ms, periodMs) {
  return ((ms % periodMs) + periodMs) % periodMs;
}

function restamp(bar, ms) {
  if (!bar) return bar;
  if (Number(bar.Epoch) === ms) return bar;
  return {
    ...bar,
    Epoch: ms,
    TimeStamp: new Date(ms).toISOString(),
  };
}

function rawVolume(bar) {
  if (bar?.TotalVolume != null && bar.TotalVolume !== '') return bar.TotalVolume;
  return bar?.Volume;
}

function pickBy(a, b, preferA) {
  const na = Number(a);
  const nb = Number(b);
  const aOk = Number.isFinite(na);
  const bOk = Number.isFinite(nb);
  if (aOk && bOk) return preferA(na, nb) ? a : b;
  if (aOk) return a;
  if (bOk) return b;
  return a ?? b;
}

function blendCandles(alignedBar, looseBar, looseMs, alignedMs) {
  const later = looseMs >= alignedMs ? looseBar : alignedBar;
  const volume = pickBy(rawVolume(alignedBar), rawVolume(looseBar), (a, b) => a >= b);
  return {
    ...alignedBar,
    Open: alignedBar.Open,
    High: pickBy(alignedBar.High, looseBar.High, (a, b) => a >= b),
    Low: pickBy(alignedBar.Low, looseBar.Low, (a, b) => a <= b),
    Close: later.Close,
    TotalVolume: volume,
    Epoch: alignedBar.Epoch,
    TimeStamp: alignedBar.TimeStamp,
    BarStatus: alignedBar.BarStatus === 'Closed' || looseBar.BarStatus === 'Closed'
      ? 'Closed'
      : alignedBar.BarStatus || looseBar.BarStatus,
  };
}

function combine(prev, next) {
  if (!prev) return next;
  if (!next) return prev;
  if (prev.bar.BarStatus === 'Closed' && next.bar.BarStatus !== 'Closed') return prev;

  const prevAligned = prev.originalMs === prev.start;
  const nextAligned = next.originalMs === next.start;
  if (next.bar.BarStatus === 'Closed' && prev.bar.BarStatus !== 'Closed' && nextAligned) {
    return next;
  }
  if (prevAligned && nextAligned) {
    return next.originalMs >= prev.originalMs ? next : prev;
  }

  const aligned = prevAligned ? prev : nextAligned ? next : (prev.originalMs <= next.originalMs ? prev : next);
  const loose = aligned === prev ? next : prev;
  return {
    start: aligned.start,
    originalMs: aligned.originalMs,
    bar: blendCandles(aligned.bar, loose.bar, loose.originalMs, aligned.originalMs),
  };
}

function store(item, start) {
  return {
    start,
    originalMs: item.ms,
    bar: item.ms === start ? item.bar : restamp(item.bar, start),
  };
}

function latestAtOrBefore(sorted, ms) {
  let lo = 0;
  let hi = sorted.length - 1;
  let ans = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] <= ms) {
      ans = sorted[mid];
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

function projectOffset(ms, periodMs, offset) {
  let projected = Math.floor(ms / periodMs) * periodMs + offset;
  if (projected > ms) projected -= periodMs;
  return projected;
}

function snapLoose(ms, periodMs, canonicalTimes, primaryOffset) {
  const prior = latestAtOrBefore(canonicalTimes, ms);
  if (prior != null && ms - prior < periodMs) return prior;
  return projectOffset(ms, periodMs, primaryOffset);
}

function finish(byStart) {
  return Array.from(byStart.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, stored]) => stored.bar);
}

/**
 * Collapse bars onto candle opens.
 * Offsets that show up at least twice are real opens (clock hours and, for
 * Yahoo 60m, the 9:30 session grid). A one-off stamp is the forming bar.
 */
export function snapBarsToPeriod(bars, periodMs) {
  const bucket = Number(periodMs);
  if (!Array.isArray(bars) || bars.length === 0) return [];
  if (!Number.isFinite(bucket) || bucket <= 0) return bars;

  const items = [];
  for (const bar of bars) {
    const ms = toMs(bar);
    if (!Number.isFinite(ms)) continue;
    items.push({ bar, ms });
  }
  if (!items.length) return [];

  const counts = new Map();
  for (const item of items) {
    const off = offsetOf(item.ms, bucket);
    counts.set(off, (counts.get(off) || 0) + 1);
  }
  // Repeated offsets are real candle opens (Yahoo 60m uses both :00 and :30).
  // A single bar exactly on the epoch or half-period grid is an open too —
  // the forming bar is the one that lands somewhere else.
  const half = bucket / 2;
  const repeated = [...counts.entries()]
    .filter(([off, count]) => count >= 2 || off === 0 || off === half)
    .map(([off]) => off);

  if (!repeated.length) {
    const groups = new Map();
    for (const item of items) {
      const key = Math.floor(item.ms / bucket) * bucket;
      const list = groups.get(key);
      if (list) list.push(item);
      else groups.set(key, [item]);
    }
    const byStart = new Map();
    for (const list of groups.values()) {
      if (list.length === 1) {
        byStart.set(list[0].ms, store(list[0], list[0].ms));
        continue;
      }
      const start = Math.min(...list.map((item) => item.ms));
      let acc = null;
      for (const item of list) acc = combine(acc, store(item, start));
      byStart.set(start, acc);
    }
    return finish(byStart);
  }

  const repeatedSet = new Set(repeated);
  let primaryOffset = repeated[0];
  for (const off of repeated) {
    if (counts.get(off) > counts.get(primaryOffset)) primaryOffset = off;
  }
  const canonicalTimes = items
    .filter((item) => repeatedSet.has(offsetOf(item.ms, bucket)))
    .map((item) => item.ms)
    .sort((a, b) => a - b);

  const byStart = new Map();
  for (const item of items) {
    const off = offsetOf(item.ms, bucket);
    const start = repeatedSet.has(off)
      ? item.ms
      : snapLoose(item.ms, bucket, canonicalTimes, primaryOffset);
    byStart.set(start, combine(byStart.get(start), store(item, start)));
  }
  return finish(byStart);
}

function utcMonthIndex(ms) {
  const date = new Date(ms);
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
}

/** Forming monthly bar is stamped at "now"; fold it into that month's open. */
export function snapMonthlyBars(bars) {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  const groups = new Map();
  for (const bar of bars) {
    const ms = toMs(bar);
    if (!Number.isFinite(ms)) continue;
    const key = utcMonthIndex(ms);
    const list = groups.get(key);
    const item = { bar, ms };
    if (list) list.push(item);
    else groups.set(key, [item]);
  }
  const byStart = new Map();
  for (const list of groups.values()) {
    const start = Math.min(...list.map((item) => item.ms));
    let acc = null;
    for (const item of list) acc = combine(acc, store(item, start));
    byStart.set(start, acc);
  }
  return finish(byStart);
}

export function isMonthlyUnit(unit) {
  const u = String(unit || '').toLowerCase();
  return u === 'monthly' || u === 'month';
}

export function isDailyUnit(unit) {
  const u = String(unit || '').toLowerCase();
  return u === 'daily' || u === 'day';
}

const etDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function etDayKey(ms) {
  return etDayFormatter.format(ms);
}

/**
 * One candle per New York session date.
 * TradeStation stamps the daily close at 16:00 ET, which is 20:00 UTC in EDT
 * and 21:00 UTC in EST. A scrollback page can return the same session an hour
 * off from the first page; both offsets are "real" to a 24h grid, so they
 * would otherwise draw as two bars.
 */
export function snapDailyBars(bars) {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  const groups = new Map();
  for (const bar of bars) {
    const ms = toMs(bar);
    if (!Number.isFinite(ms)) continue;
    const key = etDayKey(ms);
    const list = groups.get(key);
    const item = { bar, ms };
    if (list) list.push(item);
    else groups.set(key, [item]);
  }
  const byStart = new Map();
  for (const list of groups.values()) {
    const start = Math.min(...list.map((item) => item.ms));
    let acc = null;
    for (const item of list) acc = combine(acc, store(item, start));
    byStart.set(start, acc);
  }
  return finish(byStart);
}

export function isWeeklyUnit(unit) {
  const u = String(unit || '').toLowerCase();
  return u === 'weekly' || u === 'week';
}

function etWeekKey(ms) {
  const [y, m, d] = etDayKey(ms).split('-').map(Number);
  const utcDay = Date.UTC(y, m - 1, d);
  const dow = new Date(utcDay).getUTCDay();
  return utcDay - ((dow + 6) % 7) * 86400000;
}

/**
 * One candle per New York trading week. The 7-day epoch grid drifts an hour
 * across DST, so the same week from two pages (or a forming stream bar) would
 * otherwise survive as separate candles.
 */
export function snapWeeklyBars(bars) {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  const groups = new Map();
  for (const bar of bars) {
    const ms = toMs(bar);
    if (!Number.isFinite(ms)) continue;
    const key = etWeekKey(ms);
    const list = groups.get(key);
    const item = { bar, ms };
    if (list) list.push(item);
    else groups.set(key, [item]);
  }
  const byStart = new Map();
  for (const list of groups.values()) {
    const start = Math.min(...list.map((item) => item.ms));
    let acc = null;
    for (const item of list) acc = combine(acc, store(item, start));
    byStart.set(start, acc);
  }
  return finish(byStart);
}

export function snapYahooBars(bars, { barMs, unit } = {}) {
  if (isMonthlyUnit(unit)) return snapMonthlyBars(bars);
  return snapBarsToPeriod(bars, barMs);
}
